// src/utils/privacy.ts
import { Location } from '../types';

export interface PrivacySettings {
  useCoarseLocation: boolean;
  allowPreciseLocation: boolean;
  stripImageMetadata: boolean;
}

export const defaultPrivacySettings: PrivacySettings = {
  useCoarseLocation: false, // Default to high precision
  allowPreciseLocation: true, // Default to high precision
  stripImageMetadata: true,
};

export const getPrivacySettings = (): PrivacySettings => {
  try {
    const stored = localStorage.getItem('instant-tours-privacy');
    if (stored) {
      return { ...defaultPrivacySettings, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.warn('Error loading privacy settings:', error);
  }
  return defaultPrivacySettings;
};

export const savePrivacySettings = (settings: PrivacySettings): void => {
  try {
    localStorage.setItem('instant-tours-privacy', JSON.stringify(settings));
  } catch (error) {
    console.warn('Error saving privacy settings:', error);
  }
};

export const sanitizeLocation = (location: Location, settings: PrivacySettings): Location => {
  if (settings.useCoarseLocation && !settings.allowPreciseLocation) {
    // Round to ~100m precision for privacy mode
    return {
      ...location,
      latitude: Math.round(location.latitude * 1000) / 1000,
      longitude: Math.round(location.longitude * 1000) / 1000,
    };
  }
  // Return precise location for accuracy mode
  return location;
};

export const hasLocationPermission = async (): Promise<boolean> => {
  if (!navigator.geolocation) {
    return false;
  }

  try {
    const permission = await navigator.permissions.query({ name: 'geolocation' });
    return permission.state === 'granted';
  } catch (error) {
    // Fallback for browsers that don't support permissions API
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve(true),
        () => resolve(false),
        { timeout: 1000 }
      );
    });
  }
};

export const hasCameraPermission = async (): Promise<boolean> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ 
      video: { facingMode: 'environment' } 
    });
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch (error) {
    return false;
  }
};

export const requestLocationPermission = (settings?: PrivacySettings): Promise<GeolocationPosition> => {
  const privacySettings = settings || getPrivacySettings();
  
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }

    // Use high accuracy for precision mode, coarse for privacy mode
    const options: PositionOptions = {
      enableHighAccuracy: privacySettings.allowPreciseLocation, // High accuracy for precision mode
      timeout: 10000,
      maximumAge: privacySettings.allowPreciseLocation ? 60000 : 300000, // Shorter cache for precision mode
    };

    navigator.geolocation.getCurrentPosition(
      resolve,
      reject,
      options
    );
  });
};